// src/pages/AddAwardPage.js
import React, { useState, useEffect } from "react";
import "./Addawards.css";
import apiClient from "../utils/apiClient";
import SessionHandler from "../components/SessionHandler";

const AddAwardPage = () => {
  const [formData, setFormData] = useState({
    employeeName: "",
    title: "",
    type: "Award",
    department: "",
    description: "",
  });

  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  /* ================= FETCH EMPLOYEES ================= */
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const data = await apiClient("/auth/allemployees/");
        if (!data) return;

        // ✅ NORMALIZE EMPLOYEE DATA
        const normalized = data.map((emp) => ({
          id: emp.id,
          name:
            emp.name ||
            `${emp.first_name || ""} ${emp.last_name || ""}`.trim() ||
            emp.username ||
            emp.email,
          department: emp.department || "",
        }));

        setEmployees(normalized);
      } catch (err) {
        console.error("Employee fetch failed:", err);
        setError("Failed to load employees.");
      }
    };

    fetchEmployees();
  }, []);

  /* ================= INPUT CHANGE ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "employeeName") {
      const filtered = employees.filter((emp) =>
        emp.name.toLowerCase().includes(value.toLowerCase())
      );

      setFilteredEmployees(filtered);
      setShowSuggestions(true);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* ================= SELECT EMPLOYEE ================= */
  const handleEmployeeSelect = (employee) => {
    setFormData({
      ...formData,
      employeeName: employee.name,
      department: employee.department,
    });

    setFilteredEmployees([]);
    setShowSuggestions(false);
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess("");
    setError("");

    try {
      await apiClient("/awards/", {
        method: "POST",
        body: JSON.stringify(formData),
      });

      setSuccess("✅ Award added successfully!");
      setFormData({
        employeeName: "",
        title: "",
        type: "Award",
        department: "",
        description: "",
      });
    } catch (err) {
      console.error(err);
      setError("❌ Failed to add award.");
    }
  };

  /* ================= UI ================= */
  return (
    <div className="award-form-container">
      <h1 className="page-title">Add Award / Recognition</h1>

      {success && <p className="success-message">{success}</p>}
      {error && <p className="error-message">{error}</p>}

      <form onSubmit={handleSubmit} className="award-form">
        {/* EMPLOYEE AUTOCOMPLETE */}
        <div className="autocomplete-container">
          <input
            type="text"
            name="employeeName"
            value={formData.employeeName}
            placeholder="Employee Name"
            onChange={handleChange}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            required
          />

          {showSuggestions && filteredEmployees.length > 0 && (
            <ul className="suggestions-list">
              {filteredEmployees.map((emp) => (
                <li
                  key={emp.id}
                  className="suggestion-item"
                  onClick={() => handleEmployeeSelect(emp)}
                >
                  {emp.name} ({emp.department})
                </li>
              ))}
            </ul>
          )}
        </div>

        <input
          type="text"
          name="title"
          value={formData.title}
          placeholder="Award Title"
          onChange={handleChange}
          required
        />

        <select name="type" value={formData.type} onChange={handleChange}>
          <option value="Award">Award</option>
          <option value="Recognition">Recognition</option>
        </select>

        <input
          type="text"
          name="department"
          value={formData.department}
          readOnly
          placeholder="Department"
          required
        />

        <textarea
          name="description"
          value={formData.description}
          placeholder="Reason / Description"
          onChange={handleChange}
          rows={4}
        />

        <button type="submit" className="submit-btn">
          Add Award
        </button>
      </form>

      <SessionHandler />
    </div>
  );
};

export default AddAwardPage;
